import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  IPublicClientApplication,
} from '@azure/msal-browser';
import {
  BdAuthErrorResponse,
  clearStoredAuthErrorResponse,
  isAuthErrorPending,
  readAuthErrorResponseFrom,
  storeAuthErrorResponse,
} from '@bindoc-projects/app-config-domain-lib';

/**
 * Gives a failed identity-provider redirect response a terminal state.
 *
 * Azure AD B2C signals a failed user flow by redirecting back with
 * `#error=…&error_description=…` instead of an authorization code. msal-browser
 * parses that fragment only while one of its own interactions is flagged in
 * progress; a response arriving without the flag — or a stale one replayed by
 * `navigateToLoginRequestUrl` — makes `handleRedirectPromiseInternal` return
 * `null` without looking at it. Nothing rejects, so nothing in the app learns
 * that the login failed.
 *
 * The MsalGuard then sees an app with no account and starts the next
 * `loginRedirect`, which runs the same failing flow again. In production that
 * showed up as a ~1.2 Hz full-page reload cycle which only ended when the user
 * abandoned the tab, with no error displayed and nothing reported
 * (Zendesk #4429 → BDA-5866).
 *
 * So the app has to notice the error response itself:
 *
 * 1. {@link captureFailedRedirect} runs at MSAL-instance creation — before any
 *    guard can evaluate — records the response for the error page, and strips it
 *    from the URL so a replay cannot re-arm the cycle.
 * 2. {@link preventLoginAfterFailedRedirect} then refuses to start another
 *    *interactive* redirect for the rest of this page load, so the MsalGuard
 *    fails instead of bouncing to the authority and takes its configured
 *    `loginFailedRoute` ('sys-login-failed').
 *
 * Scope of the block is deliberately narrow:
 * - Only `loginRedirect` / `acquireTokenRedirect` — the flows that leave the
 *   page. `ssoSilent`, `acquireTokenSilent` and popup flows are untouched, so
 *   silent token renewal and the browser-restart session rescue keep working.
 * - Only until the failure has been shown to the user. The gate is the
 *   `bd.auth.error-pending` marker, which the error page consumes on arrival, so
 *   a later user-initiated login runs normally. It lives in storage rather than
 *   in memory on purpose: an in-memory flag dies on a reload, and because the
 *   response is stripped from the URL there would be nothing left to re-detect
 *   it from — so a reload would silently re-arm the failing login.
 */
let failedRedirect: BdAuthErrorResponse | null = null;

/**
 * Detects an error response on the current URL, records it and strips it.
 *
 * Returns the response so callers can report it; `null` when this page load is
 * not a failed auth response. Exported for tests — production code gets it via
 * {@link preventLoginAfterFailedRedirect}.
 */
export function captureFailedRedirect(): BdAuthErrorResponse | null {
  if (typeof window === 'undefined') {
    return null; // SSR — there is no redirect response to inspect
  }
  // Assigned unconditionally: the flag is a statement about the URL this page
  // load started from, so a clean URL must clear a flag left by an earlier
  // instance rather than inherit it.
  const response = readAuthErrorResponseFrom(window.location);
  failedRedirect = response;
  if (!response) {
    return null;
  }
  storeAuthErrorResponse(response);
  stripAuthErrorFromUrl();
  return response;
}

/** The error response this page load came back with, if any. */
export function currentFailedRedirect(): BdAuthErrorResponse | null {
  return failedRedirect;
}

/**
 * Whether an interactive login must not be started automatically right now,
 * because a failed response is still waiting to be shown.
 */
export function isInteractiveLoginBlocked(): boolean {
  return failedRedirect !== null || isAuthErrorPending();
}

/**
 * Forgets the recorded failure — used by tests and after a successful sign-in,
 * so a later genuine re-authentication is never blocked by a stale flag.
 *
 * Clears the stored copy too: once the user is signed in, the previous failure
 * is history, and leaving it behind would make the error page report a stale
 * correlation id if it were ever reached again in this tab.
 */
export function resetFailedRedirect(): void {
  failedRedirect = null;
  clearStoredAuthErrorResponse();
}

/**
 * Removes the error parameters from the visible URL without reloading.
 *
 * Two reasons: MSAL's `navigateToLoginRequestUrl` restore was observed
 * replaying the same stale `#error=…` URL on every iteration of the production
 * loop, and a leftover fragment would otherwise block the next login attempt in
 * this tab.
 */
function stripAuthErrorFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    url.searchParams.delete('error_uri');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // history/URL unavailable — the flag above still stops the retry
  }
}

/**
 * Wraps the interactive redirect methods so they reject while this page load is
 * known to be a failed auth response, instead of restarting the failing flow.
 *
 * Rejects with the `interaction_in_progress` BrowserAuthError that msal-browser
 * itself raises for a refused interactive request, so callers (MsalGuard, the
 * MsalInterceptor) take their existing failure paths unchanged.
 */
export function preventLoginAfterFailedRedirect(
  instance: IPublicClientApplication,
): IPublicClientApplication {
  captureFailedRedirect();

  const originalAcquireTokenRedirect =
    instance.acquireTokenRedirect.bind(instance);
  const originalLoginRedirect = instance.loginRedirect.bind(instance);

  instance.acquireTokenRedirect = (request) =>
    isInteractiveLoginBlocked()
      ? rejectAfterFailedRedirect(request?.correlationId)
      : originalAcquireTokenRedirect(request);

  instance.loginRedirect = (request) =>
    isInteractiveLoginBlocked()
      ? rejectAfterFailedRedirect(request?.correlationId)
      : originalLoginRedirect(request);

  return instance;
}

function rejectAfterFailedRedirect(correlationId = ''): Promise<never> {
  return Promise.reject(
    new BrowserAuthError(
      BrowserAuthErrorCodes.interactionInProgress,
      correlationId,
    ),
  );
}
